import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Eye, Megaphone, MousePointerClick, DollarSign, Inbox, Globe2, LayoutGrid, Phone, Navigation, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { StatusBadge } from "./ui";

export default function Overview({ setTab }) {
  const [stats, setStats] = useState(null);
  const [traffic, setTraffic] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setTraffic(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="h-64 rounded-2xl bg-white animate-pulse" />;

  const cards = [
    { label: "Published listings", value: stats.listings_published, icon: ListChecks, tab: "listings" },
    { label: "Pending review", value: stats.listings_pending, icon: Inbox, tab: "listings", highlight: stats.listings_pending > 0 },
    { label: "Active ads", value: stats.ads_active, icon: Megaphone, tab: "ads" },
    { label: "Impressions", value: stats.impressions, icon: Eye, tab: "ads" },
    { label: "Ad clicks", value: stats.clicks, icon: MousePointerClick, tab: "ads" },
    { label: "Ad revenue (paid)", value: `$${stats.revenue.toLocaleString()}`, icon: DollarSign, tab: "requests" },
    { label: "Active countries", value: stats.countries_active, icon: Globe2, tab: "locations" },
    { label: "Categories", value: stats.categories, icon: LayoutGrid, tab: "categories" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => setTab(c.tab)}
            data-testid={`stat-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`text-left bg-white p-5 rounded-xl border shadow-sm hover:shadow-md transition-shadow ${c.highlight ? "border-gold" : "border-[#E6DED3]"}`}
          >
            <c.icon className={`w-5 h-5 mb-3 ${c.highlight ? "text-gold-dark" : "text-ruby"}`} />
            <p className="font-display text-2xl font-bold text-ink">{c.value}</p>
            <p className="text-xs text-stone2 mt-1">{c.label}</p>
          </button>
        ))}
      </div>

      {traffic && (
        <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6" data-testid="admin-traffic">
          <div className="flex items-center justify-between mb-5">
            <p className="font-display font-bold text-ink">Visitor traffic — all listings</p>
            <p className="text-[11px] font-mono uppercase tracking-wider text-stone2">last 7 days</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
            {[["Views", "view", Eye], ["Website", "website", MousePointerClick], ["Calls", "phone", Phone], ["Directions", "directions", Navigation], ["Shares", "share", Share2], ["Saves", "save", ListChecks]].map(([label, k, Icon]) => (
              <div key={k} className="text-center bg-sand/50 rounded-lg py-3" data-testid={`traffic-total-${k}`}>
                <Icon className="w-4 h-4 text-ruby mx-auto mb-1.5" />
                <p className="font-display text-lg font-bold text-ink">{traffic.totals_7d[k]}</p>
                <p className="text-[10px] uppercase tracking-wider text-stone2">{label}</p>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-1.5 h-20 mb-6" data-testid="traffic-chart">
            {traffic.daily.map((d) => {
              const maxV = Math.max(...traffic.daily.map((x) => x.views), 1);
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                  <div className="w-full bg-gold/80 rounded-t" style={{ height: `${Math.max((d.views / maxV) * 100, 4)}%` }} title={`${d.day}: ${d.views} views`} />
                  <span className="text-[9px] text-stone2">{d.day.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-stone2 mb-3">Top listings by views</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[560px]" data-testid="traffic-table">
              <thead>
                <tr className="border-b border-sand text-[10px] uppercase tracking-wider text-stone2">
                  <th className="py-2 pr-3">Listing</th><th className="py-2 pr-3">Views</th><th className="py-2 pr-3">Website</th><th className="py-2 pr-3">Calls</th><th className="py-2 pr-3">Directions</th><th className="py-2">Shares/Saves</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand">
                {traffic.listings.slice(0, 10).map((l) => (
                  <tr key={l.id} data-testid={`traffic-row-${l.id}`}>
                    <td className="py-2.5 pr-3 text-sm font-semibold text-ink">{l.name}<span className="block text-[10px] font-normal text-stone2">{l.category}</span></td>
                    <td className="py-2.5 pr-3 text-sm">{l.views}</td>
                    <td className="py-2.5 pr-3 text-sm">{l.website}</td>
                    <td className="py-2.5 pr-3 text-sm">{l.phone}</td>
                    <td className="py-2.5 pr-3 text-sm">{l.directions}</td>
                    <td className="py-2.5 text-sm">{l.shares_saves}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-sand">
            <p className="font-display font-bold text-ink">Pending submissions</p>
            <button onClick={() => setTab("listings")} className="text-xs font-semibold text-ruby" data-testid="overview-view-listings">View all</button>
          </div>
          <div className="divide-y divide-sand">
            {stats.recent_pending.length === 0 && <p className="px-5 py-6 text-sm text-stone2">All caught up — nothing waiting for review.</p>}
            {stats.recent_pending.map((l) => (
              <div key={l.id} className="px-5 py-3.5 flex items-center justify-between" data-testid={`pending-item-${l.id}`}>
                <div>
                  <p className="text-sm font-semibold text-ink">{l.name}</p>
                  <p className="text-xs text-stone2">{l.category_label} · {l.address?.city || "—"}</p>
                </div>
                <StatusBadge status={l.status} />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-sand">
            <p className="font-display font-bold text-ink">Recent ad requests</p>
            <button onClick={() => setTab("requests")} className="text-xs font-semibold text-ruby" data-testid="overview-view-requests">View all</button>
          </div>
          <div className="divide-y divide-sand">
            {stats.recent_ad_requests.length === 0 && <p className="px-5 py-6 text-sm text-stone2">No advertising requests yet.</p>}
            {stats.recent_ad_requests.map((r) => (
              <div key={r.id} className="px-5 py-3.5 flex items-center justify-between" data-testid={`request-item-${r.id}`}>
                <div>
                  <p className="text-sm font-semibold text-ink">{r.business_name}</p>
                  <p className="text-xs text-stone2">{r.placement.replace(/_/g, " ")} · {r.budget || "budget TBD"}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-stone2">
        Public site: <Link to="/" className="text-ruby font-semibold underline underline-offset-2" data-testid="overview-view-site">View BurmeseHub.com</Link>
      </p>
    </div>
  );
}
